/**
 * Edge AI Box catalog — standalone analytics appliances.
 *
 * These are NOT recorders and NOT cameras. They sit between existing IP
 * cameras (any brand, RTSP / ONVIF) and the user's VMS / monitoring
 * platform, running AI inference (object detection, face recognition,
 * intrusion, perimeter protection, people counting, etc.) on each video
 * stream and outputting events + metadata.
 *
 * Hybrid AI-NVRs (Dahua IVSS, Hikvision DeepinMind, Hanwha X-series,
 * Axis Camera Station S22 Mk II) are intentionally NOT in this file —
 * they belong in nvr-recorder catalogs since recording is half their job.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 *
 * Sources:
 *   - GANZ: ganzsecurity.com product pages + authorised distributor
 *     mirror (cctv.net) for ZN-AIBOX-STD spec since the ganzsecurity.com
 *     product page links the detail PDF rather than inlining it.
 *   - Hanwha Vision: hanwhavision.com global product page for AIB-800
 *     (verified directly against the manufacturer site).
 *   - Camect: camect.com main site + Embedded Works distributor product
 *     page + PSI Magazine review (cited per entry) — Camect deliberately
 *     keeps spec sheets light, distributors quote the verified figures.
 *
 * Every entry carries `datasheetUrl` + `// SRC:` comments above each
 * block listing the page actually read. Fields not in the source are
 * left undefined — never guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const AI_BOX_CATALOG: EquipmentCatalogEntry[] = [

  // ═══════════════════════════════════════════════════════════════════════
  // GANZ — AIBox V2.0 family (CBC America / Computar Ganz). NDAA-compliant.
  // Standalone deep-learning analytics; ingest RTSP/ONVIF from existing
  // cameras, push events + metadata into GANZ CORTROL VMS, Genetec
  // Security Center, NX Witness (and any ONVIF-NVR / VMS via Webhook).
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.ganzsecurity.com/products/zn-aibox-std
  // SRC: https://cctv.net/ganz-zn-aibox-std-ganz-zn-aibox-std-new-generation-ai-based-intelligent-video-analytics-solution
  //      Distributor mirror of GANZ datasheet — 8Ch STD with 16Ch extend
  //      mode, 4K max input, ONVIF + RTSP (HTTP/HTTPS), H.264 + H.265,
  //      Alarm in + Relay + Wiegand + USB + RS-485 + LAN. NDAA compliant.
  //      AI features: Human/Object Detect, Masking, Intrusion, Loitering,
  //      Counting (people/vehicle/zone), Virtual fence, Stop, Stay/Go,
  //      Occupancy, Fallen person, Animal detection, Crowd, pose
  //      estimation, smart scene detection.
  {
    id: 'ganz-zn-aibox-std',
    manufacturer: 'GANZ',
    model: 'ZN-AIBOX-STD',
    category: 'ai-box',
    tagline:
      'GANZ AIBox V2 STD — 8-ch (16-ch extend), 4K, ONVIF/RTSP, deep-learning analytics',
    verified: true,
    datasheetUrl: 'https://www.ganzsecurity.com/products/zn-aibox-std',
    specs: {
      channels: 8, // default; 16 in extended mode
      maxInputResolution: '4K',
      onvif: true,
      vmsIntegrations: [
        'GANZ CORTROL',
        'Genetec Security Center',
        'NX Witness',
      ],
      ioPorts: ['Alarm in', 'Relay', 'Wiegand', 'USB', 'RS-485'],
      networkPorts: '2× GbE LAN',
      networkPortCount: 2,
      aiFeatures: [
        'object-detection',
        'face-detection',
        'intrusion',
        'loitering',
        'people-counting',
        'vehicle-counting',
        'virtual-fence',
        'occupancy',
        'crowd-density',
        'fallen-person',
        'animal-detection',
        'pose-estimation',
        'privacy-masking',
      ],
      ndaaCompliant: true,
      formFactor: 'compact-desktop',
    },
  },

  // SRC: https://www.ganzsecurity.com/products/zn-aibox16
  //      ZN-AIBOX16 — 16-channel dedicated SKU, same V2.0 deep-learning
  //      engine + rule engine as ZN-AIBOX-STD. Compatible with existing
  //      ONVIF VMS / NVR / DVR / alarm monitoring. NDAA compliant.
  {
    id: 'ganz-zn-aibox16',
    manufacturer: 'GANZ',
    model: 'ZN-AIBOX16',
    category: 'ai-box',
    tagline:
      'GANZ AIBox V2 — 16-ch dedicated analytics, ONVIF NVR/DVR/VMS-agnostic, NDAA',
    verified: true,
    datasheetUrl: 'https://www.ganzsecurity.com/products/zn-aibox16',
    specs: {
      channels: 16,
      onvif: true,
      networkPorts: '2× GbE LAN',
      networkPortCount: 2,
      vmsIntegrations: [
        'GANZ CORTROL',
        'Genetec Security Center',
        'NX Witness',
      ],
      aiFeatures: [
        'object-detection',
        'intrusion',
        'loitering',
        'people-counting',
        'vehicle-counting',
        'virtual-fence',
        'occupancy',
      ],
      ndaaCompliant: true,
      formFactor: 'compact-desktop',
    },
  },

  // SRC: https://www.ganzsecurity.com/products/zn-aibox8
  //      ZN-AIBOX8 — 8-channel base SKU (Gen 1 / non-FR variant). Same
  //      deep-learning analytics suite without face recognition.
  {
    id: 'ganz-zn-aibox8',
    manufacturer: 'GANZ',
    model: 'ZN-AIBOX8',
    category: 'ai-box',
    tagline:
      'GANZ AIBox — 8-ch entry analytics, ONVIF/RTSP, intrusion + counting + virtual fence',
    verified: true,
    datasheetUrl: 'https://www.ganzsecurity.com/products/zn-aibox8',
    specs: {
      channels: 8,
      onvif: true,
      networkPorts: '2× GbE LAN',
      networkPortCount: 2,
      vmsIntegrations: [
        'GANZ CORTROL',
        'Genetec Security Center',
        'NX Witness',
      ],
      aiFeatures: [
        'object-detection',
        'intrusion',
        'loitering',
        'people-counting',
        'vehicle-counting',
        'virtual-fence',
        'occupancy',
      ],
      ndaaCompliant: true,
      formFactor: 'compact-desktop',
    },
  },

  // SRC: https://www.ganzsecurity.com/products/zn-aibox4
  //      ZN-AIBOX4 — 4-channel entry SKU (Gen 1). Same deep-learning
  //      engine as the 8/16-ch siblings. Optional add-on AI licences:
  //      License Plate Recognition, Abandoned Object Detection, Gender
  //      Detection, Safety Helmet Detection, Mask/Sunglass Detection,
  //      Human+Vehicle Detection, Fisheye Object Detection, Privacy
  //      Masking, Fallen Person Detection, Combined Counter Event
  //      Triggering. CE / FCC / ONVIF certified. HTML5 web config
  //      (no plugin). Compatible with existing ONVIF VMS / NVR / DVR /
  //      alarm monitoring systems.
  {
    id: 'ganz-zn-aibox4',
    manufacturer: 'GANZ',
    model: 'ZN-AIBOX4',
    category: 'ai-box',
    tagline:
      'GANZ AIBox — 4-ch entry analytics, ONVIF/RTSP, optional LPR / face / helmet licences',
    verified: true,
    datasheetUrl: 'https://www.ganzsecurity.com/products/zn-aibox4',
    specs: {
      channels: 4,
      onvif: true,
      networkPorts: '1× GbE LAN',
      networkPortCount: 1,
      vmsIntegrations: [
        'GANZ CORTROL',
        'Genetec Security Center',
        'NX Witness',
      ],
      aiFeatures: [
        'object-detection',
        'intrusion',
        'loitering',
        'people-counting',
        'vehicle-counting',
        'virtual-fence',
        'occupancy',
        'fallen-person',
        'privacy-masking',
        // Optional add-on licences (sold separately):
        'anpr-lpr',
        'face-detection',
      ],
      ndaaCompliant: true,
      formFactor: 'compact-desktop',
    },
  },

  // SRC: https://www.ganzsecurity.com/products/zn-aibox-pro
  //      ZN-AIBOX-PRO — flagship variant supporting up to 16 camera
  //      channels with "50+ intuitive AI apps capable (on-premise)" —
  //      crowd detection, heat mapping, intrusion, people counting, LPR,
  //      fallen person, animal, vehicle counting, thermal detection,
  //      queue management, zone occupancy, line crossing, occupancy
  //      counting. ONVIF + various VMS. NDAA compliant.
  {
    id: 'ganz-zn-aibox-pro',
    manufacturer: 'GANZ',
    model: 'ZN-AIBOX-PRO',
    category: 'ai-box',
    tagline:
      'GANZ AIBox PRO — 16-ch flagship, 50+ on-prem AI apps, ONVIF, NDAA',
    verified: true,
    datasheetUrl: 'https://www.ganzsecurity.com/products/zn-aibox-pro',
    specs: {
      channels: 16,
      onvif: true,
      networkPorts: '2× GbE LAN',
      networkPortCount: 2,
      vmsIntegrations: [
        'GANZ CORTROL',
        'Genetec Security Center',
        'NX Witness',
      ],
      aiFeatures: [
        'object-detection',
        'face-detection',
        'anpr-lpr',
        'intrusion',
        'loitering',
        'people-counting',
        'vehicle-counting',
        'queue-management',
        'heatmap',
        'crowd-density',
        'occupancy',
        'fallen-person',
        'animal-detection',
        'virtual-fence',
        'thermal-detection',
      ],
      ndaaCompliant: true,
      formFactor: 'compact-desktop',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // HANWHA VISION — AIB-800 standalone AI Box (peripheral). Lets non-AI
  // cameras gain server-side AI analytics. NVIDIA Jetson Orin NX 8GB SOM.
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.hanwhavision.com/en/products/peripherals/ai-box/A/aib-800/
  //      AIB-800 spec: NVIDIA ORIN NX 8GB (SOM), 8 channels @ CIF–32 MP
  //      (recommended 8CH @ Full HD 10fps), object detection
  //      (Person/Face/Vehicle/License plate), people + vehicle counting,
  //      queue management, heatmap, dynamic privacy masking. VMS:
  //      Milestone, Genetec, Wisenet SSM, Wisenet WAVE, SUNAPI SDK/CGI
  //      for 3rd-party. 1× RJ-45 1 GbE. 1 alarm in + 4 alarm out
  //      (terminal block). 28 W. 300×47.6×240.9 mm, 1.39 kg.
  //      CE / UKCA / FCC / IC / VCCI / RCM / UL.
  {
    id: 'hanwha-aib-800',
    manufacturer: 'Hanwha Vision',
    model: 'AIB-800',
    category: 'ai-box',
    tagline:
      '8-ch AI Box — NVIDIA Orin NX 8 GB, WiseAI, ANPR/face/people/vehicle, Milestone/Genetec',
    verified: true,
    datasheetUrl:
      'https://www.hanwhavision.com/en/products/peripherals/ai-box/A/aib-800/',
    specs: {
      channels: 8,
      maxInputResolution: '32 MP (recommended 8CH @ Full HD 10 fps)',
      aiProcessor: 'NVIDIA Jetson Orin NX 8 GB (SOM)',
      vmsIntegrations: [
        'Milestone',
        'Genetec Security Center',
        'Wisenet SSM',
        'Wisenet WAVE',
        'SUNAPI SDK/CGI (3rd-party)',
      ],
      onvif: true,
      ioPorts: ['1 alarm in', '4 alarm out', 'terminal block'],
      networkPorts: '1× RJ-45 1 GbE',
      networkPortCount: 1,
      powerConsumptionW: 28,
      formFactor: 'desktop', // 300×47.6×240.9 mm metal housing
      aiFeatures: [
        'face-detection',
        'face-recognition',
        'object-detection',
        'object-classification',
        'anpr-lpr',
        'people-counting',
        'vehicle-counting',
        'queue-management',
        'heatmap',
        'privacy-masking',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // CAMECT — Smart Camera Hub. Residential / light-commercial AI hub that
  // ingests RTSP / ONVIF + cloud-camera APIs (Nest, Arlo, Wyze) and runs
  // local AI for person / vehicle / animal / package detection.
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.camect.com/
  // SRC: https://embeddedworks.net/product/empc1474/
  //      Verified specs — 12 HD cameras (24 MP total) for Home edition,
  //      1 TB internal storage, 50+ camera brands (Nest/Arlo/Wyze plus
  //      ONVIF/RTSP). Linux OS. Local storage, no per-camera cloud fees.
  // SRC: https://psimagazine.co.uk/tested-camect-smart-camera-hub/
  //      PSI Magazine review — Camect Home / Pro hardware = ASUS NUC12WSH
  //      base, 19 V DC PSU. Pro supports up to 60 MP recording capacity
  //      (~30× 1080p cameras).
  {
    id: 'camect-smart-camera-hub-home',
    manufacturer: 'Camect',
    model: 'Smart Camera Hub (Home)',
    category: 'ai-box',
    tagline:
      'Residential AI hub — 12 HD cams, local 1 TB, person/vehicle/animal/package detection',
    verified: true,
    datasheetUrl: 'https://www.camect.com/',
    specs: {
      channels: 12,
      maxInputResolution: '24 MP total (~12× 1080p)',
      onvif: true,
      vmsIntegrations: ['Webhook', 'IFTTT', 'Home Assistant'],
      storageTb: 1,
      networkPorts: '1× RJ-45 GbE (ASUS NUC12WSH base)',
      networkPortCount: 1,
      formFactor: 'compact-desktop',
      osPlatform: 'Linux',
      aiFeatures: [
        'object-detection',
        'object-classification',
        'people-counting',
        'vehicle-counting',
        'animal-detection',
        'package-detection',
      ],
    },
  },

  // SRC: https://psimagazine.co.uk/tested-camect-smart-camera-hub/
  //      Camect Pro — up to 60 MP recording capacity (~30× 1080p cameras),
  //      2/4/8 TB SSD storage options, full remote access + monitoring-
  //      station integrations (Sentinel, Immix, MAS, Stages, Praxis,
  //      Manitou, Dice) + Webhook for any VMS.
  // SRC: https://store.camect.com/products/camect-home-smart-camera-hub-with-all-in-service
  {
    id: 'camect-smart-camera-hub-pro',
    manufacturer: 'Camect',
    model: 'Smart Camera Hub (Pro)',
    category: 'ai-box',
    tagline:
      'Pro AI hub — ~30× 1080p cams (60 MP), 2-8 TB SSD, monitoring-station integrations',
    verified: true,
    datasheetUrl: 'https://store.camect.com/',
    specs: {
      channels: 30, // ~30× 1080p, 60 MP recording capacity
      maxInputResolution: '60 MP total recording capacity',
      onvif: true,
      vmsIntegrations: [
        'Sentinel',
        'Immix',
        'MAS',
        'Stages',
        'Praxis',
        'Manitou',
        'Dice',
        'Webhook',
      ],
      networkPorts: '1× RJ-45 GbE (ASUS NUC12WSH base)',
      networkPortCount: 1,
      formFactor: 'compact-desktop',
      osPlatform: 'Linux',
      aiFeatures: [
        'object-detection',
        'object-classification',
        'people-counting',
        'vehicle-counting',
        'animal-detection',
        'package-detection',
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // AVIGILON (Motorola Solutions) — AI Appliance 2X. Server-side analytics
  // companion to Unity Video sites. NOT an NVR — recording stays on the
  // Avigilon NVR; the AI Appliance just consumes streams and outputs COD,
  // Appearance Search, Facial Recognition events + on-premise generative
  // AI ("Visual Alerts").
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.avigilon.com/nvr-systems/ai-appliance-2x
  //      AIA2X CG3 — max 160 COD / 160 Appearance Search / 113 Facial
  //      Recognition channels. Unity Video 8 compatible. ACC7 not
  //      compatible. On-premise generative-AI Visual Alerts.
  {
    id: 'avigilon-ai-appliance-2x-cg3',
    manufacturer: 'Avigilon',
    model: 'AI Appliance 2X (CG3)',
    category: 'ai-box',
    tagline:
      'Enterprise AI Appliance — 160 COD / 160 AS / 113 FR ch, GenAI Visual Alerts, Unity Video 8',
    verified: true,
    datasheetUrl: 'https://www.avigilon.com/nvr-systems/ai-appliance-2x',
    specs: {
      channels: 160,
      faceRecognitionChannels: 113,
      vmsIntegrations: ['Avigilon Unity Video 8'],
      aiFeatures: [
        'object-detection',
        'object-classification',
        'appearance-search',
        'face-recognition',
        'occupancy',
        'generative-ai-alerts',
      ],
      osPlatform: 'Avigilon Secure OS',
      formFactor: '2U',
    },
  },

  // SRC: https://www.avigilon.com/nvr-systems/ai-appliance-2x
  //      AIA2X CG2 — max 80 COD / 80 AS / 57 FR channels. Smaller variant
  //      for mid-size deployments. Same AI capabilities as CG3 (COD, AS,
  //      FR, Visual Alerts GenAI), Unity Video 8 compatible.
  {
    id: 'avigilon-ai-appliance-2x-cg2',
    manufacturer: 'Avigilon',
    model: 'AI Appliance 2X (CG2)',
    category: 'ai-box',
    tagline:
      'Mid-size AI Appliance — 80 COD / 80 AS / 57 FR ch, GenAI Visual Alerts, Unity Video 8',
    verified: true,
    datasheetUrl: 'https://www.avigilon.com/nvr-systems/ai-appliance-2x',
    specs: {
      channels: 80,
      faceRecognitionChannels: 57,
      vmsIntegrations: ['Avigilon Unity Video 8'],
      aiFeatures: [
        'object-detection',
        'object-classification',
        'appearance-search',
        'face-recognition',
        'occupancy',
        'generative-ai-alerts',
      ],
      osPlatform: 'Avigilon Secure OS',
      formFactor: '2U',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ADVANTECH — AIR-030 NVIDIA Jetson AGX Orin edge AI inference platform.
  // NOTE: This is a hardware platform, not a turnkey security analytics
  // appliance — installer must layer partner software (BriefCam, Vaidio,
  // IronYun, DeepStream-based pipelines, custom models). Listed here so
  // the planner can size a Jetson-class edge AI install where camera-side
  // ONVIF analytics aren't enough.
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.advantech.com/en-us/products/932c8818-07cc-4917-89e9-7a678ddc029c/air-030/mod_a08e709e-09af-4124-bf1f-42d12c1c2fb0
  //      AIR-030 (64 GB variant) — NVIDIA Jetson AGX Orin SoC (Arm
  //      Cortex-A78AE), up to 275 Sparse TOPS INT8 (64 GB) or 200 TOPS
  //      INT8 (32 GB), 64 GB eMMC, 3× RJ-45 up to 2.5 GbE, 4× COM
  //      (RS-232/422/485), 16-bit DIO, 2× CAN, 4× USB 3.2 Type-A + 1×
  //      USB-C, 9-36 VDC input, 15-60 W (64 GB) / 15-40 W (32 GB),
  //      200×220×74 mm, 3.63 kg, -10..+60 °C, Ubuntu 20.04 +
  //      NVIDIA JetPack 5.0+.
  {
    id: 'advantech-air-030',
    manufacturer: 'Advantech',
    model: 'AIR-030',
    category: 'ai-box',
    tagline:
      'NVIDIA Jetson AGX Orin edge AI platform — 275 TOPS, 3× 2.5 GbE, BYO analytics',
    verified: true,
    datasheetUrl:
      'https://www.advantech.com/en-us/products/932c8818-07cc-4917-89e9-7a678ddc029c/air-030/mod_a08e709e-09af-4124-bf1f-42d12c1c2fb0',
    specs: {
      channels: 0, // not specified — depends on partner software loaded
      aiProcessor: 'NVIDIA Jetson AGX Orin (up to 275 Sparse TOPS INT8, 64 GB)',
      networkPorts: '3× RJ-45 up to 2.5 GbE',
      ioPorts: [
        '4× COM (RS-232/422/485)',
        '16-bit DIO',
        '2× CAN',
        '4× USB 3.2 Type-A',
        '1× USB-C',
      ],
      powerConsumptionW: 60, // max for 64 GB variant; 32 GB tops out at 40 W
      formFactor: 'compact-desktop', // 200×220×74 mm, 3.63 kg
      osPlatform: 'Ubuntu 20.04 + NVIDIA JetPack 5.0+',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // AAEON — BOXER-8253AI. Rugged fanless edge AI box with two PoE PSE
  // ports — meaning it can DELIVER PoE to a pair of cameras as well as
  // ingest their streams + run analytics. Useful for small-edge sites
  // where you want one device handling power + analytics for 2 cameras.
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.aaeon.com/en/product/detail/nvidia-jetson-xavier-nx-ai-edge-poe-boxer-8253ai
  //      BOXER-8253AI — NVIDIA Jetson Xavier NX SoC (6-core Arm 64-bit,
  //      384 CUDA cores + 48 Tensor cores), up to 21 TOPS. 8 GB LPDDR4 +
  //      16 GB eMMC (expandable via microSD / mSATA / 2.5" SATA III).
  //      2× GbE PoE PSE 802.3af (60 W total budget). 4× USB 3.2 Gen 2.
  //      HDMI input for direct video analysis. Mini Card + M.2 2230 slots.
  //      Fanless, -15..+55 °C operating range.
  {
    id: 'aaeon-boxer-8253ai',
    manufacturer: 'AAEON',
    model: 'BOXER-8253AI',
    category: 'ai-box',
    tagline:
      'Fanless edge AI box — Jetson Xavier NX (21 TOPS), 2× GbE PoE PSE, HDMI input, BYO analytics',
    verified: true,
    datasheetUrl:
      'https://www.aaeon.com/en/product/detail/nvidia-jetson-xavier-nx-ai-edge-poe-boxer-8253ai',
    specs: {
      channels: 0, // BYO partner software determines channel count
      aiProcessor:
        'NVIDIA Jetson Xavier NX (6-core Arm 64-bit, 384 CUDA + 48 Tensor, 21 TOPS)',
      networkPorts: '2× GbE PoE PSE 802.3af (60 W total budget)',
      ioPorts: [
        '4× USB 3.2 Gen 2',
        'HDMI input',
        'COM',
        'DIO',
        'Mini Card',
        'M.2 2230',
      ],
      storageTb: 0.016, // 16 GB eMMC; user-expandable via microSD / mSATA / 2.5" SATA
      formFactor: 'compact-desktop',
      osPlatform: 'Linux (NVIDIA JetPack)',
    },
  },
];

/**
 * Fast set lookup for renderers / BOM code that needs to ask "is this
 * placed equipment an AI Box?" without paying the linear EQUIPMENT_CATALOG
 * scan that `getCatalogEntryById` does. The EquipmentRenderer uses this
 * to swap in the "Ai" glyph + purple disc colour for AI Box markers
 * (since `'ai-box'` category shares the `'recorder'` EquipmentType).
 */
export const AI_BOX_CATALOG_IDS: ReadonlySet<string> = new Set(
  AI_BOX_CATALOG.map((e) => e.id),
);

